/*! Copyright (c) Safe As Milk. All rights reserved. */
import { debounce } from "utils";

class ProductSingle extends HTMLElement {
    #resizeObserver;
    #product;
    #variant;
    constructor() {
        super();
        this.updateGallery = this.updateGallery.bind(this);
        this.setPdpHeight = this.setPdpHeight.bind(this);
    }
    connectedCallback() {
        this.mediaGallery = this.querySelector("media-gallery");
        this.productUrl = this.dataset.productUrl;
        this.sectionId = this.dataset.sectionId;
        this.#setProductData();

        this.updateGalleryOnVariantChange = this.hasAttribute("data-update-gallery-on-variant-change");
        if (this.updateGalleryOnVariantChange) {
            const variantSelectors = Array.from(this.querySelectorAll("variant-selects, variant-radios, variant-swatches, variant-mixed-inputs"));
            variantSelectors.forEach((selector => {
                selector.addEventListener("on:variant:change", this.updateGallery);
            }));
        }
        this.#moveModalsFromStickyScrollContainer();
        this.#resizeObserver = new ResizeObserver(debounce(this.setPdpHeight, 100));
        this.#resizeObserver.observe(this.querySelector(".product-single__box"));
        this.#setupInitialVariantFilter();
    }
    disconnectedCallback() {
        if (this.updateGalleryOnVariantChange) {
            const variantSelectors = Array.from(this.querySelectorAll("variant-selects, variant-radios, variant-swatches, variant-mixed-inputs"));
            variantSelectors.forEach((selector => {
                selector.removeEventListener("on:variant:change", this.updateGallery);
            }));
            this.abortController = null;
        }
        this.#resizeObserver.disconnect();
    }
    setPdpHeight() {
        this.style.setProperty("--pdp-height", `${this.querySelector(".product-single__box").offsetHeight}px`);
    }
    async updateGallery({detail: {variant: variant}}) {
        if (await this.#filterGalleryForVariant(variant)) return;
        const gallery = this.querySelector("media-gallery");
        if (!this.sectionId || !this.productUrl || !gallery) return;
        if (this.abortController) this.abortController.abort();
        let url = this.productUrl.includes("?") ? `${this.productUrl}&sections[]=${this.sectionId}` : `${this.productUrl}?sections[]=${this.sectionId}`;
        if (variant && !url.includes("variant=")) {
            url = `${url}&variant=${variant.id}`;
        }
        this.abortController = new AbortController;
        const data = await fetch(url, {
            signal: this.abortController.signal
        });
        const html = await data.json();
        const parsed = (new DOMParser).parseFromString(html[this.sectionId], "text/html");
        const newGallery = parsed.querySelector("media-gallery");
        gallery.replaceWith(newGallery);
        this.mediaGallery = newGallery;
        if (window.ShopifyXR) window.ShopifyXR.setupXRElements();
    }
    async updateMedia() {
        if (await this.#filterGalleryForVariant(this.#variant)) return;
        const featuredMediaId = this.#product?.variant_media_ids ? this.#product.variant_media_ids[this.#variant?.id] : this.#variant?.featured_media?.id || null;
        if (!this.mediaGallery || !featuredMediaId) return;
        this.mediaGallery.goToSlide(featuredMediaId);
    }
    set variant(variant) {
        this.#variant = variant;
        this.updateMedia();
    }
    get variant() {
        return this.#variant;
    }
    #setProductData() {
        const productData = document.getElementById(`ProductSingleMediaJson-${this.sectionId}`);
        if (productData) {
            this.#product = JSON.parse(productData.textContent);
        }
    }
    #computeActiveMediaGroup(featuredMediaId) {
        if (!this.#product || !featuredMediaId) return null;
        let variantMediaIdsArray = [];
        if (this.#product.variant_media_ids) {
            variantMediaIdsArray = Object.values(this.#product.variant_media_ids).filter(Boolean);
        } else {
            variantMediaIdsArray = (this.#product.variants || []).filter((variant => variant.featured_media)).map((variant => variant.featured_media.id));
        }
        const variantMediaIds = new Set(variantMediaIdsArray);
        let inGroup = false;
        const activeIds = new Set();
        for (const media of this.#product.media || []) {
            if (media.id === featuredMediaId) {
                inGroup = true;
            } else if (variantMediaIds.has(media.id)) {
                inGroup = false;
            }
            if (inGroup) {
                activeIds.add(media.id);
            }
        }

        return activeIds.size > 0 ? activeIds : null;
    }
    async #filterGalleryForVariant(variant) {
        await customElements.whenDefined("media-gallery");
        await customElements.whenDefined("media-carousel");
        const featuredMediaId = this.#product?.variant_media_ids ? this.#product.variant_media_ids[variant?.id] : variant?.featured_media?.id || null;
        const canFilter = Boolean(this.mediaGallery && this.mediaGallery.dataset.activeVariantOnly === "true" && featuredMediaId && this.#product && typeof this.mediaGallery.filterByMediaIds === "function");
        if (!canFilter) {
            return false;
        }
        const activeIds = this.#computeActiveMediaGroup(featuredMediaId);
        // apply filters in correct order
        this.mediaGallery.filterByMediaIds(activeIds);
        this.mediaGallery.goToSlide(featuredMediaId);
        return true;
    }

    async #setupInitialVariantFilter() {
        await customElements.whenDefined("media-gallery");
        await customElements.whenDefined("media-carousel");
        if (!this.mediaGallery || this.mediaGallery.dataset.activeVariantOnly !== "true" || !this.#product || typeof this.mediaGallery.filterByMediaIds !== "function") {
            return;
        }
        const mediaId = Number(this.mediaGallery.dataset.initialVariantMediaId || 0) || null;
        if (!mediaId) return;
        const activeIds = this.#computeActiveMediaGroup(mediaId);
        this.mediaGallery.filterByMediaIds(activeIds);
        this.mediaGallery.goToSlide(mediaId);
    }
    #moveModalsFromStickyScrollContainer() {
        const modals = this.querySelectorAll("sticky-scroll modal-dialog, sticky-scroll popup-dialog");
        modals.forEach((modal => {
            this.append(modal);
        }));
    }
}

customElements.define("product-single", ProductSingle);
//# sourceMappingURL=product-single.js.map
